/**
 * Table-slot capacity accounting.
 *
 * The platform caps a single player connection at MAX_TABLES_PER_CONNECTION (4)
 * tables. A "slot" is consumed by:
 *  - Any open table in Redux state.tables (cash, MTT, SnG — seated or
 *    observing) the player hasn't exited (isExitedTable, EN-11131).
 *  - Any pending SnG registration (status 'R' on a tournament that's in
 *    sitAndGoList) whose eventual table isn't already in state.tables.
 *  - Any other card the Current Tables strip shows: a queued table (waitlist
 *    spot), a seat listed from playerGames not loaded yet, a rebalance
 *    stand-in (countHeldTableSlots).
 *
 * MTT registrations are NOT counted: MTTs have scheduled start times and the
 * table doesn't materialise until the tournament starts — the openTable cap
 * check enforces the limit at JOIN time, not at register time.
 *
 * Extracted to pure helpers so the cap is enforced consistently from openTable
 * (table-open path), SnG registration (tournament-detail path), the strip's
 * "+" and the Observe button.
 */

// Keep in lockstep with the backend's MAX_TABLES_PER_CONNECTION
// (serverless-v2/shared/lib/dynamoHelpers.js): the client must never offer a
// table slot the server's subscribeGame would reject. Also the capacity of the
// multi-table navigation strip — one card slot per open table, with the "+"
// removed once the player is at this many. Raising it shrinks every card in
// the strip (they share a fixed band), so treat it as a UX decision too.
export const MAX_OPEN_TABLES = 4;

/**
 * What the Current Tables strip shows: its cards' gameIDs, the tournament each
 * tournament card belongs to, and which cards are queued tables (waitlist
 * spots), which hold their slot whether or not the player has exited the table
 * itself.
 */
export type ShownTableSlots = {
  gameIDs: Set<string>;
  tournamentByGameID: Map<string, string>;
  queuedGameIDs: Set<string>;
  /** The games of the cards holding each gameID's slot (see holdsSlot). */
  gamesByGameID: Map<string, Set<string>>;
};

type QueuedSpot = { gameId?: number | string; gameType?: string } | null | undefined;

/** Whether a waitlist spot names a table (and so holds a slot). */
export const isQueuedSpot = (spot: QueuedSpot): spot is NonNullable<QueuedSpot> =>
  !!spot && Number(spot.gameId) > 0;

/**
 * A queued table's slot id. It is the spot's gameID, except when an open table
 * of the OTHER game has the same gameID (texas and omaha gameIDs are separate
 * sequences, and the server keys a subscription by game + gameID): then it is
 * `game:gameID`, so the two hold a slot each rather than sharing one — and
 * the strip shows the spot as its own card rather than as that table.
 */
export const queuedSlotIdOf = (
  spot: NonNullable<QueuedSpot>,
  tables?: TableState | null
): string => {
  const gameID = String(spot.gameId);
  const openGame = tables?.[gameID]?.game?.game;
  const queuedGame = spot.gameType;
  return openGame && queuedGame && openGame !== queuedGame ? `${queuedGame}:${gameID}` : gameID;
};

/**
 * The player's queued tables (waitlist spots) as the cards the strip draws for
 * them, keyed by slot id (queuedSlotIdOf) — for the cap to read when no strip
 * is mounted.
 */
export const queuedCardsOf = (
  spotsById: Record<string, QueuedSpot> | null | undefined,
  tables?: TableState | null
): Array<{ id: string; gameID: string; game?: string }> =>
  Object.values(spotsById || {})
    .filter(isQueuedSpot)
    .map((spot) => ({
      id: queuedSlotIdOf(spot, tables),
      gameID: String(spot.gameId),
      game: spot.gameType
    }));

/**
 * The player's queued tables (waitlist spots) as slot ids (queuedSlotIdOf) —
 * the same spots the strip draws a card for, so the cap and the strip count
 * the same ones.
 */
export const queuedGameIdsOf = (
  spotsById: Record<string, QueuedSpot> | null | undefined,
  tables?: TableState | null
): string[] => queuedCardsOf(spotsById, tables).map((card) => card.id);

// The slots the Current Tables strip shows: every table and waitlist card in
// it. Published by useActiveTables, the strip's own derivation, and read by the
// cap when a join is attempted, so the cap counts what the strip shows — plus
// any open table the strip collapses (see countHeldTableSlots) (EN-11131).
//
// Keyed by the publishing hook instance: each mounted strip publishes under its
// own owner and withdraws on unmount, so a strip that is gone can't leave its
// tables behind for the cap to read. The latest live publication wins (every
// instance derives from the same store, so they agree). clearAuth drops them
// all with the rest of the member's state. With none published, the cap counts
// state.tables, pending registrations and the queued tables from the store.
const shownByOwner = new Map<symbol, ShownTableSlots>();

const canonicalId = (raw: string | number): string => {
  const n = Number(raw);
  return Number.isFinite(n) ? String(n) : String(raw);
};

type ShownCard = {
  /** The card's slot id when it isn't its gameID (a queued table's `game:gameID`). */
  id?: string;
  gameID: string | number;
  game?: string;
  tournamentId?: string | number | null;
};

/** The strip's cards, and the queued tables it shows a card for, as the cap reads them. */
export const shownTableSlotsOf = (
  cards: Iterable<ShownCard>,
  queuedGameIDs: Iterable<string | number> = []
): ShownTableSlots => {
  const shown: ShownTableSlots = {
    gameIDs: new Set(),
    tournamentByGameID: new Map(),
    queuedGameIDs: new Set(),
    gamesByGameID: new Map()
  };
  Array.from(cards).forEach(({ id, gameID, game, tournamentId }) => {
    const key = String(gameID);
    shown.gameIDs.add(key);
    if (tournamentId !== undefined && tournamentId !== null && tournamentId !== '') {
      shown.tournamentByGameID.set(key, canonicalId(tournamentId));
    }
    // A card under a game-qualified slot id holds that slot, not the gameID's.
    if (game && (id === undefined || String(id) === key)) {
      const games = shown.gamesByGameID.get(key) ?? new Set<string>();
      shown.gamesByGameID.set(key, games.add(game));
    }
  });
  Array.from(queuedGameIDs).forEach((gameID) => {
    shown.gameIDs.add(String(gameID));
    shown.queuedGameIDs.add(String(gameID));
  });
  return shown;
};

// The slot count the strip's "+" hides at, published with the strip, so the
// Observe button reads the same number without deriving the strip again.
const slotsInUseByOwner = new Map<symbol, number>();
const publicationListeners = new Set<() => void>();
const notifyPublication = (): void => publicationListeners.forEach((listener) => listener());

export const publishShownTableSlots = (
  owner: symbol,
  shown: ShownTableSlots,
  slotsInUse?: number
): void => {
  // Re-insert so this owner becomes the latest publication.
  shownByOwner.delete(owner);
  shownByOwner.set(owner, shown);
  if (slotsInUse !== undefined) slotsInUseByOwner.set(owner, slotsInUse);
  notifyPublication();
};

export const withdrawShownTableSlots = (owner: symbol): void => {
  shownByOwner.delete(owner);
  slotsInUseByOwner.delete(owner);
  notifyPublication();
};

export const readShownTableSlots = (): ShownTableSlots | null => {
  const all = Array.from(shownByOwner.values());
  return all.length ? all[all.length - 1] : null;
};

/**
 * The slots the latest published strip counts as in use (what its "+" hides
 * at), or 0 when no strip is mounted — then the cap in openTable, which counts
 * from the store, is the only gate.
 */
export const readStripSlotsInUse = (): number => {
  const latest = Array.from(shownByOwner.keys()).pop();
  return (latest && slotsInUseByOwner.get(latest)) || 0;
};

/**
 * The slots the add-table "+" hides at: the strip's held count, or more while
 * a rebalance in flight shows a stand-in (slotsInUse). One rule for the "+"
 * and the Observe button, which reads it as published.
 */
export const addTableSlotsInUse = (slotsInUse: number, heldSlots: number): number =>
  Math.max(slotsInUse, heldSlots);

/** Subscribe to strip publications (for useSyncExternalStore). */
export const subscribeShownTableSlots = (listener: () => void): (() => void) => {
  publicationListeners.add(listener);
  return () => {
    publicationListeners.delete(listener);
  };
};

/** Forget every published strip — on logout (clearAuth) and in tests. */
export const clearShownTableSlots = (): void => {
  shownByOwner.clear();
  slotsInUseByOwner.clear();
  notifyPublication();
};

type TableState = Record<
  string,
  {
    game?: {
      gameID?: number | string;
      game?: string;
      tabletype?: string;
      tournamentId?: number | string | null;
    };
  }
>;

const isTournamentTabletype = (tabletype: unknown): boolean =>
  tabletype === 't' || tabletype === 'm';

/**
 * Whether the open slice keyed by a Sit & Go's tournamentId is that Sit &
 * Go's waiting table (SitGo.id === tournamentId) — not an unrelated table
 * whose gameID happens to equal the tournamentId (texas/omaha gameIDs and
 * tournamentIds are separate sequences). A slice that says it is a cash table,
 * or names another tournament, is not it.
 */
const isWaitingTableOf = (slice: TableState[string] | undefined, tournamentId: string): boolean => {
  const game = slice?.game;
  if (!game) return false;
  if (game.tabletype !== undefined && !isTournamentTabletype(game.tabletype)) return false;
  const owner = game.tournamentId;
  if (owner !== undefined && owner !== null && owner !== '') {
    return canonicalId(owner) === canonicalId(tournamentId);
  }
  return true;
};
type SngListEntry = {
  tournamentId?: number | string;
  id?: number | string;
  // The SnG's table gameID, when the lobby list carries it. Used to match a
  // still-registering SnG (whose detail has no assigned gameID) to its already-
  // open waiting table when that table is keyed by the gameID, not the tournamentId.
  gameID?: number | string;
};
type PlayerTournamentDetail = {
  tournamentId: number | string;
  status?: string;
  gameID?: number | string;
};

/**
 * A Sit & Go registration that holds a slot for a table not open yet.
 * `tableKnown`: the reserved gameID is the table's own (the lobby's or the
 * details' gameID). When neither names one, `gameID` is a registration id
 * (registrationSlotId) that holds the slot but is not a table, so it can never
 * be mistaken for one or opened. Defensive: the engine's lobby feed only lists
 * a Sit & Go with an open table, so its row always carries a gameID.
 */
export type PendingSng = { gameID: string; tournamentId: string; tableKnown: boolean };

/** The slot id of a registration whose table isn't known: never a gameID. */
export const registrationSlotId = (tournamentId: string): string => `sng:${tournamentId}`;

export type SlotUsage = {
  openIds: Set<string>;
  pendingSngCount: number;
  /** Each pending SnG registration's slot: its table, or a registration id. */
  pendingSngs: PendingSng[];
  total: number;
  isAlreadyOpen: (gameID: number | string) => boolean;
  hasPendingSngSlot: (gameID: number | string) => boolean;
};

/**
 * @param isExcluded tables the player has exited. Pass the same predicate the
 *   nav strip uses (isExitedTable) so the cap and the "Current Tables" count
 *   agree: an exited table's slice can linger in, or be briefly re-added to,
 *   state.tables while the leave settles, and the nav already hides it. Every
 *   exit path sends UnsubscribeFromGameUpdates with the mark, so the server
 *   stops counting it once that lands — the client has no acknowledgement of
 *   it (EN-11223 covers surfacing the server's refusal) (EN-11131).
 */
export function countUsedTableSlots(
  tables: TableState | null | undefined,
  sitAndGoList: SngListEntry[] | null | undefined,
  playerTournamentDetails: PlayerTournamentDetail[] | null | undefined,
  isExcluded: (gameID: string) => boolean = () => false
): SlotUsage {
  const openIds = new Set<string>(
    Object.entries(tables || {})
      .filter(([id, t]) => t?.game?.gameID && t.game.gameID !== 0 && !isExcluded(id))
      .map(([id]) => id)
  );

  const sngTournamentIds = new Set<string>(
    (sitAndGoList || []).map((s) => String(s.tournamentId ?? s.id ?? '')).filter((id) => id !== '')
  );

  // tournamentId → the SnG's table gameID (when the lobby list carries it), so a
  // registering SnG can be matched to its open waiting table even when that table
  // is keyed by the assigned gameID rather than the tournamentId.
  const sngGameIdByTournament = new Map<string, string>();
  (sitAndGoList || []).forEach((s) => {
    const tid = String(s.tournamentId ?? s.id ?? '');
    const gid = s.gameID === undefined || s.gameID === null ? '' : String(s.gameID);
    if (tid && gid && gid !== '0' && gid !== '-1') sngGameIdByTournament.set(tid, gid);
  });

  // Pending SnG = registered ('R') for an SnG whose table isn't already in
  // state.tables. The table-open path always counts toward openIds first;
  // pendingSngCount only adds slots not already accounted for there.
  const pendingSngGameIds = new Set<string>();
  const pendingSngs: PendingSng[] = [];
  const pendingSngCount = (playerTournamentDetails || []).filter((d) => {
    if (d.status !== 'R') return false;
    const tournamentId = String(d.tournamentId);
    if (!sngTournamentIds.has(tournamentId)) return false;
    const assignedId = d.gameID === undefined || d.gameID === null ? null : String(d.gameID);
    // Already counted as an open table — skip to avoid double-counting.
    //  • Once running, the detail carries the real gameID, which keys the table.
    //  • While still REGISTERING the detail has no assigned gameID (gameID -1),
    //    but the player may already have the SnG's waiting table open — and that
    //    table is keyed in state.tables by the SnG's tournamentId (SitGo.id ===
    //    tournamentId). Matching that here is what stops a registering SnG from
    //    counting twice (open + pending) and falsely tripping the table cap.
    if (assignedId !== null && openIds.has(assignedId)) return false;
    if (openIds.has(tournamentId) && isWaitingTableOf(tables?.[tournamentId], tournamentId)) {
      return false;
    }
    // …or keyed by the gameID the lobby list assigns to this SnG.
    const listGameId = sngGameIdByTournament.get(tournamentId);
    if (listGameId && openIds.has(listGameId)) return false;
    // Opening this waiting table converts its reservation into an open slot.
    // Prefer the lobby game ID used by Join Table; never treat an unrelated
    // table with a matching tournament ID as the reserved table.
    const tableGameId =
      listGameId || (assignedId && assignedId !== '0' && assignedId !== '-1' ? assignedId : null);
    if (tableGameId) {
      pendingSngGameIds.add(tableGameId);
      pendingSngs.push({ gameID: tableGameId, tournamentId, tableKnown: true });
    } else {
      // No table to name: the slot is held under a registration id, never the
      // tournamentId, which a table's gameID could equal.
      pendingSngs.push({
        gameID: registrationSlotId(tournamentId),
        tournamentId,
        tableKnown: false
      });
    }
    return true;
  }).length;

  return {
    openIds,
    pendingSngCount,
    pendingSngs,
    total: openIds.size + pendingSngCount,
    isAlreadyOpen: (gameID) => openIds.has(String(gameID)),
    hasPendingSngSlot: (gameID) => pendingSngGameIds.has(String(gameID))
  };
}

export type HeldTableSlots = {
  /** gameIDs holding a slot: the strip's cards plus every open table. */
  held: Set<string>;
  /** Pending SnG registrations with no card of their own in `held`. */
  unshownSngs: PendingSng[];
  /** Slots in use — what the cap checks and the "+" button hides at. */
  total: number;
};

/**
 * The one table count the cap and the strip's "+" share (EN-11131).
 *
 * The strip's cards (`shown`) plus every non-exited table in state.tables
 * (`usage.openIds`). The union matters: subscribeGame counts every
 * subscription, and mid-rebalance the strip collapses a tournament to one
 * card while both its source and destination are still subscribed — counting
 * the strip alone would let a join through that the server refuses with a 409
 * the client can't surface.
 *
 * A pending SnG registration adds a slot unless the strip already has a card
 * holding one for it, matched by its reserved gameID or its tournament: a
 * just-started SnG can be listed from playerGames under its real gameID while
 * the player's details still say 'R' (no gameID yet), and it must not count
 * twice. A card that holds no slot (an exited table, including the
 * registration's own exit-marked table) is no match: the registration is
 * then counted as pending, once.
 *
 * @param isExcluded the same exit predicate countUsedTableSlots took. The
 *   strip publishes after it renders, so a table exited since (a rebalance
 *   marks the old seat and opens the new one in one tick) can still be in
 *   `shown`; it holds no slot, so it is dropped here too — unless it is a
 *   queued table, whose spot holds the slot on its own.
 */
export function countHeldTableSlots(
  shown: ShownTableSlots | null,
  usage: SlotUsage,
  isExcluded: (gameID: string) => boolean = () => false
): HeldTableSlots {
  const queued = shown?.queuedGameIDs ?? new Set<string>();
  const shownHeld = Array.from(shown?.gameIDs ?? []).filter(
    (id) => queued.has(id) || !isExcluded(id)
  );
  const held = new Set<string>([...shownHeld, ...Array.from(usage.openIds)]);
  const heldTournaments = new Set<string>(
    shownHeld
      .map((id) => shown?.tournamentByGameID.get(id))
      .filter((tid): tid is string => tid !== undefined)
  );
  const unshownSngs = usage.pendingSngs.filter(
    (sng) => !held.has(sng.gameID) && !heldTournaments.has(canonicalId(sng.tournamentId))
  );
  // Every pending registration has an entry in pendingSngs; one matched by a
  // card that holds a slot is already in `held`, so only the rest add one.
  return {
    held,
    unshownSngs,
    total: held.size + unshownSngs.length
  };
}

/**
 * Whether opening `game`'s table `gameID` uses a slot already held (`held`,
 * from countHeldTableSlots) rather than taking a new one. Matched by game as
 * well as gameID, as the server keys a subscription: a queued omaha table 900
 * holds no slot for texas table 900. A gameID held by no card of known game
 * (an open table the strip collapses) matches any game.
 */
export function holdsTableSlot(
  shown: ShownTableSlots | null,
  held: ReadonlySet<string>,
  gameID: string | number,
  game?: string
): boolean {
  const id = String(gameID);
  if (game && held.has(`${game}:${id}`)) return true;
  if (!held.has(id)) return false;
  const games = shown?.gamesByGameID.get(id);
  return !game || !games || games.has(game);
}
